import React, { useState } from 'react';
import { motion } from 'framer-motion';

const CTASection = () => {
  const [hoveredButton, setHoveredButton] = useState(null);
  const calUrl = 'https://Cal.com/ragib-imtiyaz';

  const handleCtaClick = () => {
    window.open(calUrl, '_blank');
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 40, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { 
        duration: 0.7, 
        ease: [0.23, 1, 0.320, 1] 
      },
    },
  };

  return (
    <section className="relative py-16 md:py-20 px-4 md:px-8 lg:px-0 overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-0 right-0 w-80 h-80 bg-gradient-to-br from-blue-200/30 to-transparent rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-gradient-to-tr from-blue-200/30 to-transparent rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-5xl mx-auto relative z-10">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={containerVariants}
          className="relative rounded-2xl overflow-hidden backdrop-blur-xl p-8 md:p-12"
          style={{
            background: 'linear-gradient(135deg, rgba(24, 44, 212, 0.95) 0%, rgba(37, 99, 235, 0.95) 100%)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
          }}
        >
          {/* Grid pattern background */}
          <div className="absolute inset-0 opacity-5">
            <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                  <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="0.5"/>
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#grid)" />
            </svg>
          </div>

          <div className="relative z-20">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              {/* Left Content - Short */}
              <motion.div className="space-y-4" variants={containerVariants}>
                <motion.div
                  variants={itemVariants}
                  className="inline-flex items-center gap-2"
                >
                  <span className="text-2xl">⚡</span>
                  <span className="text-sm font-semibold text-white uppercase tracking-widest">Transform Your Business</span>
                </motion.div>

                <motion.h2 
                  variants={itemVariants}
                  className="text-3xl md:text-4xl font-bold text-white leading-tight"
                >
                  Ready to Elevate Your Digital Presence?
                </motion.h2>
              </motion.div>

              {/* Right Content - Full Details */}
              <motion.div className="space-y-6" variants={containerVariants}>
                <motion.p 
                  variants={itemVariants}
                  className="text-base text-white/80 leading-relaxed"
                >
                  Join 50+ innovative brands that have transformed their business with our strategic digital solutions.
                </motion.p>

                {/* Features List */}
                <motion.div
                  variants={itemVariants}
                  className="space-y-3"
                >
                  {[
                    { icon: '✓', text: 'Free personalized strategy session' },
                    { icon: '✓', text: 'Custom quote in 24 hours' },
                    { icon: '✓', text: 'No hidden costs or obligations' },
                  ].map((feature, index) => (
                    <motion.div
                      key={index}
                      whileHover={{ x: 6 }}
                      className="flex items-center gap-3 text-white/90"
                    >
                      <div className="flex-shrink-0 w-5 h-5 rounded-full bg-white/20 flex items-center justify-center">
                        <span className="text-xs font-bold">{feature.icon}</span>
                      </div>
                      <span className="text-sm font-medium">{feature.text}</span>
                    </motion.div>
                  ))}
                </motion.div>

                {/* CTA Button */}
                <motion.button
                  variants={itemVariants}
                  onClick={handleCtaClick}
                  onMouseEnter={() => setHoveredButton(1)}
                  onMouseLeave={() => setHoveredButton(null)}
                  whileHover={{ y: -3, boxShadow: "0 15px 30px rgba(0, 0, 0, 0.2)" }}
                  whileTap={{ scale: 0.98 }}
                  className="relative overflow-hidden group w-full px-6 py-4 rounded-xl font-semibold text-base text-blue-600 bg-white shadow-lg transition-all duration-300 flex items-center justify-center gap-2 cursor-pointer"
                >
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-blue-50 to-white opacity-0 group-hover:opacity-100"
                    animate={{ x: hoveredButton === 1 ? 0 : -100 }}
                    transition={{ duration: 0.3 }}
                  />
                  <span className="relative z-10 text-lg">💡</span>
                  <span className="relative z-10">Get Free Strategy Session</span>
                  <motion.svg
                    className="w-4 h-4 relative z-10"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    animate={{ x: hoveredButton === 1 ? 3 : 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </motion.svg>
                </motion.button>

                {/* Trust badges */}
                <motion.div
                  variants={itemVariants}
                  className="flex items-center gap-2 text-white/70 text-xs"
                >
                  <span className="text-sm">⭐⭐⭐⭐⭐</span>
                  <span>Rated 4.9/5 by our clients</span>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default CTASection;